/**
 * Settings → Ad Account. Apple Search Ads: два способа подключения —
 * Sign in with Apple и публичный ключ (ТЗ apple_signin_connect §11, макет
 * `Apple Sign-in Connect.dc.html`, кадры 1–10).
 *
 * - Не подключено и флаг доступен — переключатель Sign in with Apple /
 *   Upload Public Key, по умолчанию Apple. Без флага — экран ключа.
 * - `apple_pending` в адресе — выбор Campaign Groups после возврата из Apple.
 * - `apple_error` в адресе — баннер ошибки. Оба параметра после показа
 *   убираются из адреса.
 * - Подключено через Apple — Method / Access / Connected, без блоков ключа.
 * - Подключено по ключу — прежние блоки ключа и ссылка «Switch to Sign in
 *   with Apple» при доступном флаге.
 * - Доступ отозван в Apple — красный баннер с кнопкой входа.
 */

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import {
  useApplePending,
  useAsaCampaignGroups,
  useAsaIntegration,
  useCancelApplePending,
  useConnectApplePending,
  useDeleteAsaIntegration,
  useRegenerateAsaKeypair,
  useSaveAsaIntegration,
  useStartAppleOAuth,
  useTestAsaConnection,
} from "@/lib/api-hooks";
import type { AsaIntegrationResponse } from "@/lib/types";
import { StConfirmModal } from "./_shared";
import { AsaBillingCard } from "./AsaBillingCard";
import { AsaCampaignGroupsCard } from "./AsaCampaignGroupsCard";
import {
  APPLE_ERROR_TEXT,
  appleSteps,
  chooserSummary,
  defaultPrimary,
  isAppleError,
} from "@/lib/ad-account-apple";
import { AccessPill, AppleLogo, CheckMark } from "./ad-account-ui";

const KEY_DESC =
  "Upload the public key below to Apple's ASA portal to obtain a Key ID, then fill in the four Apple-side identifiers. The matching private key is stored encrypted on our servers — it never leaves here.";
const SETUP_DESC =
  "Connect your Apple Ads account to sync campaigns and reporting into this workspace.";
const APPLE_DESC =
  "Connected with Sign in with Apple. Choose which Campaign Groups to sync and set up billing below.";

function errMsg(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { error?: { message?: string } } } })?.response
      ?.data?.error?.message ?? fallback
  );
}


function AppleButton({ onClick, busy, style }: { onClick: () => void; busy?: boolean; style?: React.CSSProperties }) {
  return (
    <button type="button" className="aa-apple-btn" onClick={onClick} disabled={busy} style={style}>
      <AppleLogo />
      Sign in with Apple
    </button>
  );
}


function ErrorBanner({ text }: { text: string }) {
  return (
    <div className="aa-banner aa-banner-neg">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b23030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 auto", marginTop: 1 }}>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <div className="aa-banner-text">{text}</div>
    </div>
  );
}

export function AdAccountTab() {
  const query = useAsaIntegration();
  const groupsQuery = useAsaCampaignGroups();
  const remove = useDeleteAsaIntegration();
  const start = useStartAppleOAuth();
  const [params, setParams] = useSearchParams();

  const data = query.data;
  const connected = data?.is_connected ?? false;
  const appleMethod = data?.auth_method === "apple_oauth";
  const revoked = !!data?.revoked;
  const available = !!data?.apple_oauth_available;

  const pendingId = params.get("apple_pending");
  const [shownError, setShownError] = useState<string | null>(null);
  const [seg, setSeg] = useState<"apple" | "key">("apple");
  const [confirmOff, setConfirmOff] = useState(false);
  const [startErr, setStartErr] = useState<string | null>(null);

  // apple_error показывается один раз и убирается из адреса.
  const consumed = useRef(false);
  useEffect(() => {
    const err = params.get("apple_error");
    if (!err || consumed.current) return;
    consumed.current = true;
    if (isAppleError(err)) setShownError(APPLE_ERROR_TEXT[err]);
    const next = new URLSearchParams(params);
    next.delete("apple_error");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const clearPending = () => {
    const next = new URLSearchParams(params);
    next.delete("apple_pending");
    setParams(next, { replace: true });
  };

  const onSignIn = async () => {
    setStartErr(null);
    try {
      window.location.href = await start.mutateAsync();
    } catch (e) {
      setStartErr(errMsg(e, "Could not start Sign in with Apple."));
    }
  };

  const groups = groupsQuery.data ?? [];
  const anyReadOnly =
    (data?.access_level ?? "read_write") === "read_only" ||
    groups.some((g) => g.enabled && g.access_level === "read_only");

  const showSetup = !connected && !revoked && !pendingId;
  const showAppleSetup = showSetup && available && seg === "apple";
  const showKeyCards =
    (showSetup && (!available || seg === "key")) || (connected && !appleMethod);

  const desc = pendingId
    ? SETUP_DESC
    : connected || revoked
      ? appleMethod
        ? APPLE_DESC
        : KEY_DESC
      : showAppleSetup
        ? SETUP_DESC
        : KEY_DESC;

  return (
    <div className="aa">
      <div className="aa-head">
        <div style={{ flex: "1 1 0%", minWidth: 0 }}>
          <h2 className="aa-h2">Apple Search Ads</h2>
          <div className="aa-desc">{desc}</div>
        </div>
        {(connected || revoked) && (
          <div className="aa-head-right">
            <button type="button" className="aa-btn aa-btn-danger" onClick={() => setConfirmOff(true)}>
              Disconnect
            </button>
            {appleMethod && (
              <div className="aa-head-note">
                To fully revoke access, also remove OneAds in Apple Ads → Account Settings → API.
              </div>
            )}
          </div>
        )}
      </div>

      {showSetup && available && (
        <div className="aa-seg">
          <button type="button" className={seg === "apple" ? "on" : ""} onClick={() => setSeg("apple")}>
            Sign in with Apple
          </button>
          <button type="button" className={seg === "key" ? "on" : ""} onClick={() => setSeg("key")}>
            Upload Public Key
          </button>
        </div>
      )}

      {showAppleSetup && (
        <div className="aa-card aa-card-24" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {(shownError || startErr) && <ErrorBanner text={(shownError || startErr) as string} />}
          <div className="aa-apple-grid">
            <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "flex-start" }}>
              <div className="aa-lead">The quickest way to connect your Apple Ads account. No keys or IDs needed.</div>
              <AppleButton onClick={() => void onSignIn()} busy={start.isPending} />
              <div className="aa-hint">
                Requires an Apple Ads Account Admin or Campaign Group Manager. If you don&apos;t have this role, use{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setSeg("key");
                  }}
                  className="aa-link"
                >
                  Upload Public Key
                </a>
                .
              </div>
            </div>
            <div className="aa-how">
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>How it works</div>
              {appleSteps.map((s) => (
                <div key={s.n} className="aa-step">
                  <span className="aa-step-n">{s.n}</span>
                  <div className="aa-step-t">{s.t}</div>
                </div>
              ))}
              <div className="aa-how-foot">
                Choose Read &amp; Write access to let OneAds rules manage bids and keywords. Read-only access allows reporting only.
              </div>
            </div>
          </div>
        </div>
      )}

      {pendingId && <AppleChooser pendingId={pendingId} onDone={clearPending} />}

      {connected && appleMethod && anyReadOnly && (
        <div className="aa-banner aa-banner-warn aa-mb">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c88407" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 auto" }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div style={{ flex: 1, fontSize: 13, color: "#6b4604" }}>
            <b style={{ fontWeight: 600 }}>Read-only access.</b> Reporting works, but rules can&apos;t run on this account. Reconnect with Read &amp; Write access to enable rules.
          </div>
          <button type="button" className="aa-btn aa-btn-warn" onClick={() => void onSignIn()}>
            Reconnect
          </button>
        </div>
      )}

      {revoked && (
        <div className="aa-banner aa-banner-neg aa-mb" style={{ flexDirection: "column", alignItems: "flex-start", gap: 12, padding: "14px 16px" }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b23030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 auto", marginTop: 1 }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div className="aa-banner-text">Access was revoked in Apple Ads. Rules and data sync are paused. Reconnect to resume.</div>
          </div>
          <AppleButton onClick={() => void onSignIn()} busy={start.isPending} style={{ marginLeft: 26 }} />
        </div>
      )}

      {(connected || revoked) && data && (
        <StatusCard data={data} available={available} onSwitch={() => void onSignIn()} busy={start.isPending} />
      )}

      {(connected || revoked) && <AsaCampaignGroupsCard appleMethod={appleMethod} />}
      {(connected || revoked) && <AsaBillingCard />}

      {showKeyCards && <KeyCards connected={connected} />}

      <StConfirmModal
        open={confirmOff}
        title="Disconnect Apple Search Ads?"
        body="Data collection will stop and historical metrics will become read-only."
        danger
        onCancel={() => setConfirmOff(false)}
        onConfirm={() => {
          setConfirmOff(false);
          void remove.mutate();
        }}
      />
    </div>
  );
}

function StatusCard({
  data,
  available,
  onSwitch,
  busy,
}: {
  data: AsaIntegrationResponse;
  available: boolean;
  onSwitch: () => void;
  busy: boolean;
}) {
  const apple = data.auth_method === "apple_oauth";
  const up = data.is_connected && !data.revoked;
  return (
    <div className="aa-card aa-card-20 aa-mb">
      <div style={{ display: "flex", alignItems: "center", gap: 14, justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="aa-logo-tile">
            <AppleLogo />
          </div>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>Connection status</div>
            <div className="aa-sub" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Status:
              {up ? (
                <span className="aa-pill aa-pill-pos">
                  <span className="aa-dot" />
                  Connected
                </span>
              ) : (
                <span className="aa-pill aa-pill-neg">
                  <span className="aa-dot" />
                  Disconnected
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          {up ? (
            <span className="aa-run">
              <span className="aa-run-dot aa-run-on" />
              Running
            </span>
          ) : (
            <span className="aa-run">
              <span className="aa-run-dot" />
              Paused
            </span>
          )}
          <div style={{ fontSize: 12, marginTop: 4, color: "var(--c-text-3)" }}>
            {data.last_collect_at
              ? `Last collector run: ${new Date(data.last_collect_at).toLocaleString()}`
              : "Awaiting first collector run…"}
          </div>
        </div>
      </div>
      <div className="aa-facts">
        <div className="aa-k">Method</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontWeight: 600, color: "var(--c-text)" }}>{apple ? "Sign in with Apple" : "Public Key"}</span>
          {(apple || available) && (
            <button type="button" className="aa-btn aa-btn-dark aa-btn-apple" onClick={onSwitch} disabled={busy}>
              <AppleLogo size={13} />
              {apple ? "Reconnect with Apple" : "Switch to Sign in with Apple"}
            </button>
          )}
        </div>
        {apple && (
          <>
            <div className="aa-k">Access</div>
            <div>
              <AccessPill level={data.access_level} />
            </div>
            <div className="aa-k">Connected</div>
            <div style={{ color: "var(--c-text-2)" }}>
              {data.apple_connected_at ? new Date(data.apple_connected_at).toLocaleString() : "—"}
            </div>
          </>
        )}
        {data.key_retained_until_oauth_sync && (
          <>
            <div className="aa-k">Status</div>
            <div style={{ color: "var(--c-text-2)" }}>Switching to Sign in with Apple — finishing first sync</div>
          </>
        )}
      </div>
    </div>
  );
}

function AppleChooser({ pendingId, onDone }: { pendingId: string; onDone: () => void }) {
  const pending = useApplePending(pendingId);
  const connect = useConnectApplePending();
  const cancel = useCancelApplePending();
  const accounts = pending.data?.accounts ?? [];
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [primary, setPrimary] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current || !pending.data) return;
    seeded.current = true;
    setChecked(new Set(pending.data.accounts.filter((a) => !a.blocked).map((a) => a.asa_org_id)));
  }, [pending.data]);

  const order = accounts.map((a) => a.asa_org_id);
  const effPrimary = defaultPrimary(order, checked, primary);
  const summary = chooserSummary(accounts, checked, effPrimary);

  if (pending.isError) {
    return (
      <div className="aa-card aa-card-24 aa-mb">
        <ErrorBanner text="The authorization link expired. Please sign in with Apple again." />
        <div style={{ marginTop: 14 }}>
          <button type="button" className="aa-btn" onClick={onDone}>
            Back
          </button>
        </div>
      </div>
    );
  }

  const onConnect = async () => {
    if (effPrimary == null) return;
    setErr(null);
    try {
      await connect.mutateAsync({ pendingId, selected: [...checked], primary: effPrimary });
      onDone();
    } catch (e) {
      setErr(errMsg(e, "Could not connect."));
    }
  };
  const onCancel = async () => {
    try {
      await cancel.mutateAsync(pendingId);
    } catch {
      /* истёкшая запись — тоже конец выбора */
    }
    onDone();
  };

  return (
    <div className="aa-card aa-card-24 aa-mb">
      <h3 className="aa-h3">Choose Campaign Groups to connect</h3>
      <div className="aa-sub" style={{ marginTop: 4 }}>
        Your Apple account has access to these Campaign Groups. Select the ones to sync into this workspace.
      </div>
      {err && (
        <div style={{ marginTop: 14 }}>
          <ErrorBanner text={err} />
        </div>
      )}
      <div className="aa-rows" style={{ marginTop: 16 }}>
        {pending.isLoading && <div className="aa-row aa-sub">Loading…</div>}
        {accounts.map((a) => {
          const on = checked.has(a.asa_org_id);
          const isP = on && effPrimary === a.asa_org_id;
          const toggle = () => {
            if (a.blocked) return;
            setChecked((prev) => {
              const next = new Set(prev);
              if (next.has(a.asa_org_id)) next.delete(a.asa_org_id);
              else next.add(a.asa_org_id);
              return next;
            });
          };
          return (
            <div key={a.asa_org_id} className={`aa-row${a.blocked ? " aa-row-blocked" : ""}`}>
              <button type="button" className={`aa-cb${on ? " on" : ""}`} onClick={toggle} disabled={a.blocked} aria-pressed={on}>
                {on && <CheckMark />}
              </button>
              <div className="aa-row-name" onClick={toggle}>
                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{a.name}</span>
                <span className="aa-org">Org ID {a.asa_org_id}</span>
                {a.blocked && <span className="aa-blocked">Already connected to another OneAds workspace. Disconnect it there to connect it here.</span>}
              </div>
              <div>
                <AccessPill level={a.access_level} />
              </div>
              <div>
                {on && (
                  <button type="button" className="aa-primary" onClick={() => setPrimary(a.asa_org_id)} style={{ color: isP ? "var(--c-text)" : "var(--c-text-3)" }}>
                    <span className={`aa-radio${isP ? " on" : ""}`}>
                      <span />
                    </span>
                    Primary
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18 }}>
        <button type="button" className="aa-btn aa-btn-dark" disabled={checked.size === 0 || connect.isPending} onClick={() => void onConnect()}>
          Connect
        </button>
        <button type="button" className="aa-btn" onClick={() => void onCancel()}>
          Cancel
        </button>
        {accounts.length > 0 && accounts.every((a) => a.blocked) ? (
          <span className="aa-blocked-all">
            Nothing to connect: every Campaign Group of this Apple account is already connected to another OneAds workspace.
          </span>
        ) : (
          <span className="aa-sub" style={{ marginLeft: 6 }}>{summary}</span>
        )}
      </div>
    </div>
  );
}


function KeyCards({ connected }: { connected: boolean }) {
  const query = useAsaIntegration();
  const save = useSaveAsaIntegration();
  const test = useTestAsaConnection();
  const regen = useRegenerateAsaKeypair();
  const data = query.data;

  const [editing, setEditing] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [vals, setVals] = useState({ clientId: "", teamId: "", keyId: "", orgId: "" });

  const fromData = () =>
    setVals({
      clientId: data?.client_id ?? "",
      teamId: data?.team_id ?? "",
      keyId: data?.key_id ?? "",
      orgId: data?.asa_org_id != null ? String(data.asa_org_id) : "",
    });
  useEffect(() => {
    if (data) fromData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onSave = async () => {
    setSaveErr(null);
    try {
      await save.mutateAsync({
        client_id: vals.clientId.trim(),
        team_id: vals.teamId.trim(),
        key_id: vals.keyId.trim(),
        asa_org_id: Number(vals.orgId) || 0,
      });
      setEditing(false);
    } catch (e) {
      setSaveErr(errMsg(e, "Save failed."));
    }
  };

  const onTest = async () => {
    setTestResult(null);
    try {
      const r = await test.mutateAsync();
      setTestResult(r.ok ? `OK · ${r.message ?? "connected"}` : `Failed · ${r.message ?? ""}`);
    } catch (e) {
      setTestResult(`Failed · ${errMsg(e, "Test failed")}`);
    }
  };

  const onCopyPubKey = async () => {
    if (!data?.public_key_pem) return;
    await navigator.clipboard.writeText(data.public_key_pem);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const editable = editing || !connected;

  return (
    <>
      {data?.public_key_pem && (
        <div className="aa-card aa-card-20 aa-mb">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>Public Key</div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn icon={I.copy} onClick={() => void onCopyPubKey()}>
                {copied ? "Copied!" : "Copy"}
              </Btn>
              <Btn icon={I.refresh} onClick={() => setConfirmRegen(true)} disabled={regen.isPending}>
                Regenerate
              </Btn>
            </div>
          </div>
          <pre className="aa-pre">{data.public_key_pem}</pre>
          <div style={{ fontSize: 12, marginTop: 8, color: "var(--c-text-3)" }}>
            The private key is stored encrypted on our servers and never returned in plaintext.
          </div>
        </div>
      )}

      <div className="aa-card aa-card-20">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>Apple-side identifiers</div>
          {connected &&
            (editing ? (
              <div style={{ display: "flex", gap: 8 }}>
                <Btn
                  onClick={() => {
                    fromData();
                    setEditing(false);
                  }}
                >
                  Cancel
                </Btn>
                <Btn variant="primary" icon={I.check} onClick={() => void onSave()} disabled={save.isPending}>
                  Save
                </Btn>
              </div>
            ) : (
              <Btn icon={I.edit} onClick={() => setEditing(true)}>
                Edit
              </Btn>
            ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {(
            [
              { k: "clientId", label: "Client ID", ph: "SEARCHADS.xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
              { k: "teamId", label: "Team ID", ph: "SEARCHADS.xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
              { k: "keyId", label: "Key ID", ph: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
              { k: "orgId", label: "ASA Org ID", ph: "1234567" },
            ] as const
          ).map((f) => (
            <div key={f.k} className="aa-idrow">
              <div className="aa-idlabel">{f.label}</div>
              <input
                className="input mono"
                disabled={!editable}
                placeholder={f.ph}
                value={vals[f.k]}
                onChange={(e) => setVals((v) => ({ ...v, [f.k]: e.target.value }))}
              />
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontSize: 12 }}>
            {saveErr && <span style={{ color: "var(--c-neg-600)" }}>✗ {saveErr}</span>}
            {test.isPending && <span className="muted">Testing connection…</span>}
            {!test.isPending && testResult && (
              <span style={{ color: testResult.startsWith("OK") ? "var(--c-pos-600)" : "var(--c-neg-600)" }}>
                {testResult.startsWith("OK") ? "✓ " : "✗ "}
                {testResult}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {!connected && (
              <Btn variant="primary" onClick={() => void onSave()} disabled={save.isPending}>
                Save
              </Btn>
            )}
            <Btn onClick={() => void onTest()} disabled={test.isPending}>
              Test Connection
            </Btn>
          </div>
        </div>
      </div>

      <StConfirmModal
        open={confirmRegen}
        title="Regenerate keypair?"
        body="A new public/private key pair will be generated. You'll need to upload the new public key to Apple's portal and update the Key ID. Until then, ASA data collection will fail."
        danger
        onCancel={() => setConfirmRegen(false)}
        onConfirm={() => {
          setConfirmRegen(false);
          void regen.mutate();
        }}
      />
    </>
  );
}
